import { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { LogOut } from "lucide-react";
import { cn } from "@weco/ui";

import "../modules/homework/homework.css";
import { CheckOutDialog } from "../modules/homework/components/Workday";
import { useWorkday } from "../modules/homework/hooks/useWorkday";
import { clockTime } from "../modules/homework/lib/workday";

/**
 * Suite header: Check out, while checked in to HomeWork
 * (design: weco2/homework-desk.jsx `HeaderCheckOutButton`, placed before the
 * work-status control in weco2/shell.jsx). "Go to my desk" moved into the
 * HomeWork module's own header; this is the one HomeWork control that lives in
 * the global chrome, because leaving for the day can happen from any app.
 *
 * Only rendered where HomeWork is available to this user and organization
 * (`enabled`), and only once today's check-in says you are in.
 */
export function HeaderCheckOutButton({ compact = false, enabled = true }: { compact?: boolean; enabled?: boolean }) {
  const { t } = useTranslation();
  const { data: workday } = useWorkday(enabled);
  const [open, setOpen] = useState(false);
  if (!enabled || !workday || workday.status !== "in") return null;

  const label = t("shell.checkOut.label", { defaultValue: "Check out" });
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={label}
        title={t("shell.checkOut.title", {
          time: clockTime(workday.first_checked_in_at),
          defaultValue: "Checked in at {{time}} · check out",
        })}
        className={cn(
          "relative flex items-center gap-2 rounded-full shrink-0 transition-all active:scale-95 glass-bar text-foreground hover:bg-foreground/5",
          compact ? "w-9 h-9 justify-center" : "ps-3 pe-3.5",
        )}
        style={compact ? undefined : { height: 35 }}
      >
        <LogOut size={16} strokeWidth={2} className="rtl:-scale-x-100" aria-hidden />
        {!compact && <span className="text-[13px] font-semibold whitespace-nowrap">{label}</span>}
      </button>
      {open &&
        createPortal(
          <div className="hw" style={{ position: "fixed", inset: 0, zIndex: 200, background: "transparent" }}>
            <CheckOutDialog workday={workday} onClose={() => setOpen(false)} />
          </div>,
          document.body,
        )}
    </>
  );
}
